import { useId, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { m } from "@/paraglide/messages";
import { getLocale } from "@/paraglide/runtime";
import { countText, keywordDensity } from "../lib/textstats";

const SAMPLE =
  "The quick brown fox jumps over the lazy dog. Pack my box with five dozen liquor jugs.";

function timeLabel(sec: number): string {
  if (sec < 1) return m.wc_lt_1min();
  const minutes = Math.floor(sec / 60);
  const seconds = sec % 60;
  if (minutes === 0) return m.wc_sec({ seconds: String(seconds) });
  if (seconds === 0) return m.wc_min({ minutes: String(minutes) });
  return m.wc_min_sec({ minutes: String(minutes), seconds: String(seconds) });
}

function Stat({
  label,
  value,
  testid,
}: {
  label: string;
  value: string;
  testid?: string;
}) {
  return (
    <div className="rounded-xl bg-card/40 p-4">
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd
        data-testid={testid}
        className="mt-1 font-display text-xl font-semibold tabular-nums"
      >
        {value}
      </dd>
    </div>
  );
}

export function WordCounter() {
  const uid = useId();
  const [text, setText] = useState(SAMPLE);
  const locale = getLocale();
  const stats = useMemo(() => countText(text), [text]);
  const density = useMemo(() => keywordDensity(text, 5), [text]);
  const fmt = (n: number) => n.toLocaleString(locale);

  return (
    <div className="space-y-6">
      <div className="space-y-3 rounded-2xl border border-border bg-card/60 p-6 sm:p-8">
        <div className="flex items-center justify-between">
          <Label htmlFor={`${uid}-text`}>{m.wc_input_label()}</Label>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setText("")}
          >
            {m.wc_clear()}
          </Button>
        </div>
        <textarea
          id={`${uid}-text`}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={m.wc_placeholder()}
          className="min-h-[12rem] w-full resize-y rounded-lg border border-border bg-background p-4 text-sm leading-relaxed"
        />
      </div>

      <div className="rounded-2xl bg-primary/10 p-5 text-center">
        <p className="text-xs uppercase tracking-wide text-muted-foreground">
          {m.wc_words()}
        </p>
        <p
          data-testid="wc-words"
          className="mt-1 font-display text-4xl font-bold tabular-nums text-primary"
        >
          {fmt(stats.words)}
        </p>
      </div>

      <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat
          label={m.wc_characters()}
          value={fmt(stats.characters)}
          testid="wc-characters"
        />
        <Stat
          label={m.wc_characters_no_spaces()}
          value={fmt(stats.charactersNoSpaces)}
        />
        <Stat label={m.wc_sentences()} value={fmt(stats.sentences)} />
        <Stat label={m.wc_paragraphs()} value={fmt(stats.paragraphs)} />
        <Stat
          label={m.wc_reading_time()}
          value={timeLabel(stats.readingTimeSec)}
        />
        <Stat
          label={m.wc_speaking_time()}
          value={timeLabel(stats.speakingTimeSec)}
        />
        <Stat label={m.wc_avg_word_length()} value={fmt(stats.avgWordLength)} />
        <Stat label={m.wc_longest_word()} value={stats.longestWord || "—"} />
      </dl>

      {density.length > 0 && (
        <section className="rounded-2xl border border-border bg-card/40 p-6">
          <h2 className="mb-4 font-display text-lg font-bold tracking-tight">
            {m.wc_density_title()}
          </h2>
          <ul className="space-y-2">
            {density.map((k) => (
              <li
                key={k.word}
                className="flex items-center justify-between text-sm"
              >
                <span className="font-medium">{k.word}</span>
                <span className="tabular-nums text-muted-foreground">
                  {fmt(k.count)} · {k.percent}%
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
